import React, { useState, useEffect } from 'react'
import api from '@/lib/api'
import {
  Link2,
  Copy,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Clock,
  RefreshCw,
  Zap,
  Mic,
  Info
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import { FieldMappingEditor, TargetVariable, DiscoveredField } from './FieldMappingEditor'

interface WebhookSetupWizardProps {
  open: boolean
  onClose: () => void
  onComplete: (webhook: any) => void
  existingWebhook?: any  // For editing/resuming setup
}

type WizardStep = 'create' | 'url' | 'map' | 'assistant' | 'complete'

interface VoiceAssistant {
  id: string
  name: string
  voiceId: string
  firstMessage: string
}

export function WebhookSetupWizard({ open, onClose, onComplete, existingWebhook }: WebhookSetupWizardProps) {
  const brand = useBrand()
  const brandBaseName = splitBrandName(brand.appName)[0]
  const [step, setStep] = useState<WizardStep>('create')
  const [isLoading, setIsLoading] = useState(false)
  const [webhook, setWebhook] = useState<any>(existingWebhook || null)
  const [targetVariables, setTargetVariables] = useState<TargetVariable[]>([])

  // Form state for step 1
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  // Mapping state for step 3
  const [discoveredFields, setDiscoveredFields] = useState<DiscoveredField[]>([])
  const [mappableFields, setMappableFields] = useState<DiscoveredField[]>([])
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({})
  const [fieldLabels, setFieldLabels] = useState<Record<string, string>>({})
  const [delaySeconds, setDelaySeconds] = useState(0)

  // Test submission state
  const [testSending, setTestSending] = useState(false)

  // Assistant selection state for step 4
  const [assistants, setAssistants] = useState<VoiceAssistant[]>([])
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null)
  const [loadingAssistants, setLoadingAssistants] = useState(false)

  const { toast } = useToast()

  // Fetch sample data (target variables + mappable fields) for an existing webhook
  const loadSampleData = async (webhookId: string) => {
    try {
      const response = await api.get(`/api/lead-webhooks/${webhookId}/sample`)
      if (response.data.success) {
        if (response.data.targetVariables) {
          setTargetVariables(response.data.targetVariables)
        }
        if (response.data.mappableFields?.length > 0) {
          setMappableFields(response.data.mappableFields)
        }
        if (response.data.discoveredFields?.length > 0) {
          setDiscoveredFields(response.data.discoveredFields)
        }
        // Load auto-detected mappings if none set yet
        if (Object.keys(fieldMappings).length === 0) {
          if (response.data.fieldMappings && Object.keys(response.data.fieldMappings).length > 0) {
            setFieldMappings(response.data.fieldMappings)
          }
          if (response.data.fieldLabels && Object.keys(response.data.fieldLabels).length > 0) {
            setFieldLabels(response.data.fieldLabels)
          }
        }
      }
    } catch (error) {
      console.error('Error loading sample data:', error)
    }
  }

  // Initialize from existing webhook if provided
  useEffect(() => {
    if (existingWebhook) {
      setWebhook(existingWebhook)
      setName(existingWebhook.name)
      setDescription(existingWebhook.description || '')
      setDelaySeconds(existingWebhook.delaySeconds || 0)
      setSelectedAssistantId(existingWebhook.voiceAssistantId || null)

      const hasSample = existingWebhook.samplePayload || existingWebhook.discoveredFields?.length > 0

      // Determine starting step based on setup mode
      if (existingWebhook.setupMode === 'configuring' || existingWebhook.setupMode === 'listening') {
        if (hasSample) {
          // Sample already captured — go straight to field mapping
          setStep('map')
          setDiscoveredFields(existingWebhook.discoveredFields || [])
          setFieldMappings(existingWebhook.fieldMappings || {})
          setFieldLabels(existingWebhook.fieldLabels || {})
          // Fetch target variables and mappable fields from the sample endpoint
          loadSampleData(existingWebhook.id)
        } else {
          // No sample yet — show the URL step so user can copy link again
          setStep('url')
        }
      } else if (existingWebhook.setupMode === 'active') {
        // Already active — show map step for editing
        setStep('map')
        setDiscoveredFields(existingWebhook.discoveredFields || [])
        setFieldMappings(existingWebhook.fieldMappings || {})
        loadSampleData(existingWebhook.id)
      } else {
        setStep('create')
      }

      if (existingWebhook.targetVariables) {
        setTargetVariables(existingWebhook.targetVariables)
      }
    }
  }, [existingWebhook])

  // Load assistants when entering assistant selection step
  useEffect(() => {
    if (step === 'assistant' && assistants.length === 0) {
      loadAssistants()
    }
  }, [step])

  const loadAssistants = async (): Promise<VoiceAssistant[]> => {
    setLoadingAssistants(true)
    try {
      const response = await api.get('/api/voice/assistants')
      if (response.data.success) {
        const fetched = response.data.assistants || []
        setAssistants(fetched)
        // Auto-select if only one assistant
        if (fetched.length === 1) {
          setSelectedAssistantId(fetched[0].id)
        }
        return fetched
      }
      return []
    } catch (error) {
      console.error('Error loading assistants:', error)
      return []
    } finally {
      setLoadingAssistants(false)
    }
  }

  const handleCreateWebhook = async () => {
    if (!name.trim()) {
      toast({
        title: 'Please enter a name',
        description: 'Give your form connection a name to help you identify it later',
        variant: 'destructive'
      })
      return
    }

    setIsLoading(true)
    try {
      const response = await api.post('/api/lead-webhooks', { name, description })
      if (response.data.success) {
        setWebhook(response.data.webhook)
        setTargetVariables(response.data.targetVariables || [])
        setStep('url')
        toast({
          title: 'Connection Created',
          description: 'Copy the link and add it to your form builder'
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create connection',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseAfterCreate = () => {
    onComplete(webhook)
    handleClose()
  }

  const handleSaveMappings = async () => {
    // Validate phone mapping
    if (!fieldMappings['lead.phone']) {
      toast({
        title: 'Phone field required',
        description: 'Please match a form field to the phone number',
        variant: 'destructive'
      })
      return
    }

    // Check if we should show assistant selection
    // Use return value from loadAssistants to avoid stale React state
    const currentAssistants = assistants.length > 0 ? assistants : await loadAssistants()

    // If org has multiple assistants, show selection step
    // If only one, skip to complete (or proceed with saving)
    if (currentAssistants.length > 1 && !selectedAssistantId) {
      setStep('assistant')
      return
    }

    // Save and activate
    await saveMappingsAndActivate()
  }

  const saveMappingsAndActivate = async () => {
    if (!webhook) return

    setIsLoading(true)
    try {
      const response = await api.put(`/api/lead-webhooks/${webhook.id}/field-mappings`, {
        mappings: fieldMappings,
        labels: fieldLabels,
        delaySeconds,
        voiceAssistantId: selectedAssistantId || (assistants.length === 1 ? assistants[0].id : null)
      })
      if (response.data.success) {
        setStep('complete')
        setWebhook(response.data.webhook)
        toast({
          title: 'Connection Activated!',
          description: `Your form is now connected to ${brandBaseName}`
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save settings',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleAssistantSelection = async () => {
    if (!selectedAssistantId && assistants.length > 1) {
      toast({
        title: 'Please select an assistant',
        description: 'Choose which AI voice should handle calls from this form',
        variant: 'destructive'
      })
      return
    }
    await saveMappingsAndActivate()
  }

  const handleComplete = () => {
    onComplete(webhook)
    handleClose()
  }

  const handleClose = () => {
    setStep('create')
    setName('')
    setDescription('')
    setWebhook(null)
    setDiscoveredFields([])
    setMappableFields([])
    setFieldMappings({})
    setDelaySeconds(0)
    setTestSending(false)
    setAssistants([])
    setSelectedAssistantId(null)
    onClose()
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast({
      title: 'Copied!',
      description: 'Connection link copied to clipboard'
    })
  }

  const handleTestSubmission = async () => {
    if (!webhook?.id) return
    setTestSending(true)
    try {
      const response = await api.post(`/api/lead-webhooks/${webhook.id}/test-submission`)
      if (response.data.success) {
        toast({
          title: 'Pipeline Verified!',
          description: 'The connection is working. Now submit your real form to capture its actual fields.'
        })
        // Stay on URL step — the webhook has been auto-reset and is awaiting the real form submission
        onComplete(webhook)
      }
    } catch (error: any) {
      toast({
        title: 'Test Failed',
        description: error.response?.data?.message || 'Could not send test submission',
        variant: 'destructive'
      })
    } finally {
      setTestSending(false)
    }
  }

  const renderStepIndicator = () => {
    // Two contexts: initial creation flow vs resuming after sample captured
    const isInitialFlow = step === 'create' || step === 'url'
    const steps = isInitialFlow
      ? [
          { key: 'create', label: 'Name' },
          { key: 'url', label: 'Get Link' }
        ]
      : [
          { key: 'map', label: 'Match Fields' },
          ...(assistants.length > 1 ? [{ key: 'assistant', label: 'Choose Assistant' }] : []),
          { key: 'complete', label: 'All Set!' }
        ]

    const visibleSteps = steps

    const currentIndex = visibleSteps.findIndex(s => s.key === step)

    return (
      <div className="flex items-center justify-center gap-2 mb-6">
        {visibleSteps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className={`flex items-center gap-2 ${
              i <= currentIndex ? 'text-primary' : 'text-muted-foreground'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                i < currentIndex
                  ? 'bg-primary text-primary-foreground'
                  : i === currentIndex
                    ? 'bg-primary/20 text-primary border-2 border-primary'
                    : 'bg-muted text-muted-foreground'
              }`}>
                {i < currentIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
            </div>
            {i < visibleSteps.length - 1 && (
              <div className={`w-8 h-0.5 ${
                i < currentIndex ? 'bg-primary' : 'bg-muted'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    )
  }

  const renderCreateStep = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="connection-name">Connection Name</Label>
        <Input
          id="connection-name"
          placeholder="e.g., Contact Page Form"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="connection-description">Description (optional)</Label>
        <Textarea
          id="connection-description"
          placeholder="What form is this connection for?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
      </div>

      <Card className="bg-muted/50 border-dashed">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <div className="text-sm">
              <p className="font-medium">Automatic Field Detection</p>
              <p className="text-muted-foreground mt-1">
                After creating this connection, you'll submit a test form.
                We'll automatically detect all the fields and let you match them.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleCreateWebhook} disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create & Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  )

  const renderUrlStep = () => (
    <div className="space-y-6">
      {/* Connection Link */}
      <div className="space-y-2">
        <Label>Your Connection Link</Label>
        <div className="flex gap-2">
          <Input
            value={webhook?.webhookUrl || ''}
            readOnly
            className="font-mono text-sm"
          />
          <Button
            variant="outline"
            onClick={() => copyToClipboard(webhook?.webhookUrl || '')}
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Link
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Copy this link and paste it into your form builder's webhook or integration settings
        </p>
      </div>

      {/* Instructions */}
      <Card className="border-dashed">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Quick Setup Guide</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex gap-3">
            <Badge variant="outline" className="h-6 w-6 p-0 justify-center flex-shrink-0">1</Badge>
            <span>Copy the connection link above</span>
          </div>
          <div className="flex gap-3">
            <Badge variant="outline" className="h-6 w-6 p-0 justify-center flex-shrink-0">2</Badge>
            <span>Go to your form builder (Gravity Forms, Typeform, Jotform, etc.)</span>
          </div>
          <div className="flex gap-3">
            <Badge variant="outline" className="h-6 w-6 p-0 justify-center flex-shrink-0">3</Badge>
            <span>Add the link as a webhook endpoint</span>
          </div>
        </CardContent>
      </Card>

      {/* Send Test Submission */}
      <Card className="bg-orange-500/5 border-orange-500/20">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <Zap className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm flex-1">
              <p className="font-medium text-orange-700 dark:text-orange-300">Not sure if it's connected?</p>
              <p className="text-muted-foreground mt-1">
                Send a quick test to verify the pipeline is working. This won't capture any fields — you'll still need to submit your real form so we can detect its actual fields.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={handleTestSubmission}
                disabled={testSending}
              >
                {testSending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="mr-2 h-4 w-4" />
                )}
                {testSending ? 'Sending...' : 'Send Test Submission'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification info card */}
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-700 dark:text-blue-300">We'll notify you when we receive the first submission</p>
              <p className="text-muted-foreground mt-1">
                You can close this wizard and come back later. When someone submits your form,
                we'll automatically detect the fields and you can finish setup.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setStep('create')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={handleCloseAfterCreate}>
          Done
          <CheckCircle className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  )

  const renderMapStep = () => (
    <div className="space-y-6">
      <FieldMappingEditor
        discoveredFields={mappableFields.length > 0 ? mappableFields : discoveredFields.filter(
          f => f.type === 'string' || f.type === 'number' || f.type === 'boolean'
        )}
        targetVariables={targetVariables}
        mappings={fieldMappings}
        labels={fieldLabels}
        onChange={(newMappings, newLabels) => {
          setFieldMappings(newMappings)
          setFieldLabels(newLabels)
        }}
      />

      {/* Delay Configuration */}
      <div className="space-y-2">
        <Label htmlFor="delay-seconds" className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Wait Time Before Call
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id="delay-seconds"
            type="number"
            min="0"
            max="300"
            value={delaySeconds}
            onChange={(e) => setDelaySeconds(parseInt(e.target.value) || 0)}
            className="w-24"
          />
          <span className="text-sm text-muted-foreground">seconds</span>
        </div>
        <p className="text-xs text-muted-foreground">
          How long to wait after receiving a submission before calling. Use 0 for immediate calls.
        </p>
      </div>

      {/* Re-capture option */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="h-4 w-4" />
        <span>Wrong fields?</span>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={async () => {
            try {
              await api.put(`/api/lead-webhooks/${webhook?.id}/reset`)
              toast({
                title: 'Connection Reset',
                description: 'Submit a new form to capture different fields'
              })
              onComplete(webhook)
              handleClose()
            } catch (error) {
              console.error('Error resetting webhook:', error)
            }
          }}
        >
          Reset and capture a new submission
        </Button>
      </div>

      <div className="flex justify-end pt-4">
        <Button onClick={handleSaveMappings} disabled={isLoading || !fieldMappings['lead.phone']}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {assistants.length > 1 ? 'Continue' : 'Save & Activate'}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  )

  const renderAssistantStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold">Who should handle these calls?</h3>
        <p className="text-muted-foreground mt-1">
          Choose which AI voice will call leads from this form
        </p>
      </div>

      {loadingAssistants ? (
        <div className="py-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="mt-2 text-muted-foreground">Loading assistants...</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {assistants.map((assistant) => (
            <Card
              key={assistant.id}
              className={`cursor-pointer transition-colors hover:border-primary/50 ${
                selectedAssistantId === assistant.id
                  ? 'border-primary bg-primary/5'
                  : ''
              }`}
              onClick={() => setSelectedAssistantId(assistant.id)}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      selectedAssistantId === assistant.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}>
                      <Mic className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium">{assistant.name}</p>
                      <p className="text-sm text-muted-foreground capitalize">
                        {assistant.voiceId} voice
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedAssistantId === assistant.id && (
                      <CheckCircle className="h-5 w-5 text-primary" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setStep('map')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={handleAssistantSelection} disabled={isLoading || (!selectedAssistantId && assistants.length > 1)}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save & Activate
          <Zap className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  )

  const renderCompleteStep = () => (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center py-6">
        <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
          <CheckCircle className="h-8 w-8 text-green-500" />
        </div>
        <h3 className="text-xl font-semibold">All Set!</h3>
        <p className="text-muted-foreground mt-2 max-w-md">
          Your form is now connected. When visitors submit your form, they'll automatically receive a call from {brandBaseName}.
        </p>
      </div>

      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Status</span>
            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Active</Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Call Delay</span>
            <span className="text-sm font-medium">
              {delaySeconds > 0 ? `${delaySeconds} seconds` : 'Immediate'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Fields Matched</span>
            <span className="text-sm font-medium">
              {Object.values(fieldMappings).filter(Boolean).length}
            </span>
          </div>
          {selectedAssistantId && assistants.length > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">AI Assistant</span>
              <span className="text-sm font-medium">
                {assistants.find(a => a.id === selectedAssistantId)?.name || 'Default'}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-center pt-4">
        <Button onClick={handleComplete}>
          Done
        </Button>
      </div>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            {step === 'create' && 'Connect a Form'}
            {step === 'url' && 'Your Connection Link'}
            {step === 'map' && 'Match Your Form Fields'}
            {step === 'assistant' && 'Choose Your AI Assistant'}
            {step === 'complete' && 'Connection Ready'}
          </DialogTitle>
          <DialogDescription>
            {step === 'create' && 'Set up a connection to automatically call leads from form submissions'}
            {step === 'url' && 'Copy this link and add it to your form builder'}
            {step === 'map' && 'Tell us which form fields contain lead information'}
            {step === 'assistant' && 'Select the AI voice that will call your leads'}
            {step === 'complete' && 'Your form connection is configured and ready'}
          </DialogDescription>
        </DialogHeader>

        {renderStepIndicator()}

        {step === 'create' && renderCreateStep()}
        {step === 'url' && renderUrlStep()}
        {step === 'map' && renderMapStep()}
        {step === 'assistant' && renderAssistantStep()}
        {step === 'complete' && renderCompleteStep()}
      </DialogContent>
    </Dialog>
  )
}
